import { all, call, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { showErrorNotification } from '../actions/App';
import { getBusListError, getCarrierBusList } from '../actions/Buses';
import * as actions from '../actions/Carriers';
import { getCarrierDriverList, getDriverListError } from '../actions/Drivers';
import * as carriersApi from '../api/Carriers';
import { history } from '../store';
import {
  GET_CARRIER_DETAILS,
  GET_CARRIER_DETAILS_WITH_BUSES_AND_DRIVERS,
  GET_CARRIER_LIST,
  UPDATE_CARRIER_DETAILS,
  CREATE_CARRIER,
  REMOVE_CARRIER,
} from '../constants/ActionTypes';

const storedCarriersSelector = state => state.carriers.carriers;

function* filterCarriers(carriers) {
  const { recentlyAdded } = yield select(storedCarriersSelector);

  if (!recentlyAdded.length) {
    return carriers;
  }

  return carriers.filter(carrier => recentlyAdded.find(added => added.id !== carrier.id));
}

function* getCarrierList({ payload }) {
  yield put(actions.setCarrierListLoading(true));

  try {
    const { data } = yield call(carriersApi.getCarrierList, payload.pageNumber, payload.size);
    const filteredCarriers = yield call(filterCarriers, data.entities);
    yield put(actions.getCarrierListSuccess(filteredCarriers, data.pageCount));
  } catch (error) {
    yield put(showErrorNotification(error.messages));
  }

  yield put(actions.setCarrierListLoading(false));
}

function* getCarrierDetails({ payload }) {
  yield put(actions.setCarrierDetailsLoading(true));

  try {
    const { data } = yield call(carriersApi.getCarrierDetails, payload.carrierId);

    yield put(actions.getCarrierDetailsSuccess(data));
  } catch (error) {
    yield put(actions.getCarrierDetailsError());
  } finally {
    yield put(actions.setCarrierDetailsLoading(false));
  }
}

function* getCarrierDetailsWithBusesAndDrivers({ payload: { carrierId } }) {
  yield put(actions.setCarrierDetailsLoading(true));

  try {
    const { data } = yield call(carriersApi.getCarrierDetails, carrierId);
    yield put(actions.getCarrierDetailsSuccess(data));
    yield put(getCarrierBusList(carrierId));
    yield put(getCarrierDriverList(carrierId));
  } catch (error) {
    yield put(actions.getCarrierDetailsError());
    yield put(getBusListError());
    yield put(getDriverListError());
  } finally {
    yield put(actions.setCarrierDetailsLoading(false));
  }
}

function* createCarrier({ payload }) {
  try {
    const { data } = yield call(carriersApi.createCarrier, payload.carrier);
    const carrier = { ...data, newCarrier: true };
    yield put(actions.createCarrierSuccess(carrier));
  } catch (error) {
    yield put(actions.updateCarrierFailure());
    yield put(showErrorNotification(error.messages));
  }
}

function* updateCarrierDetails({ payload }) {
  try {
    const { data } = yield call(carriersApi.updateCarrier, payload.carrierId, payload.carrier);

    yield put(actions.updateCarrierDetailsSuccess(data));
  } catch (error) {
    yield put(actions.updateCarrierFailure());
    yield put(showErrorNotification(error.messages));
  }
}

function* removeCarrier({ payload }) {
  try {
    yield call(carriersApi.removeCarrier, payload.carrierId);
    yield put(actions.removeCarrierSuccess(payload.carrierId));
    yield call(history.push, '/routes/carriers');
  } catch (error) {
    yield put(actions.updateCarrierFailure());
    yield put(showErrorNotification(error.messages));
  }
}

export function* getCarrierListSaga() {
  yield takeEvery(GET_CARRIER_LIST, getCarrierList);
}

export function* getCarrierDetailsSaga() {
  yield takeEvery(GET_CARRIER_DETAILS, getCarrierDetails);
}

export function* getCarrierDetailsWithBusesAndDriversSaga() {
  yield takeLatest(
    GET_CARRIER_DETAILS_WITH_BUSES_AND_DRIVERS,
    getCarrierDetailsWithBusesAndDrivers,
  );
}

export function* createCarrierSaga() {
  yield takeEvery(CREATE_CARRIER, createCarrier);
}

export function* updateCarrierDetailsSaga() {
  yield takeEvery(UPDATE_CARRIER_DETAILS, updateCarrierDetails);
}

export function* removeCarrierSaga() {
  yield takeEvery(REMOVE_CARRIER, removeCarrier);
}

export default function* rootSaga() {
  yield all([
    fork(getCarrierListSaga),
    fork(getCarrierDetailsSaga),
    fork(getCarrierDetailsWithBusesAndDriversSaga),
    fork(createCarrierSaga),
    fork(updateCarrierDetailsSaga),
    fork(removeCarrierSaga),
  ]);
}
